/**
 * PublishingCardAB — right-rail publishing summary for an Audiobook.
 *
 *   • Type   — always "Audiobook" pill
 *   • Parent — category title (must be an audiobook category)
 *   • Chapters — rows the app lists (inactive ones skipped)
 *   • Length — humanized total duration when chapters carry
 *              session_length_in_sec on the loaded payload
 *   • Quotes — count
 *
 * Plus an Activate/Deactivate link in the foot, same recipe as the
 * other publishing cards.
 */
import React from 'react';
import RailCard, { RailCardHead } from '../../redesign/edit/RailCard';
import { humanizeDuration } from '../../../utils/timeFormat';

export default function PublishingCardAB({
  state,
  parentCategoryTitle,
  chapterCount,
  totalLengthSec,
  quoteCount,
  onToggleActive,
}) {
  const isActive = !!state.subcategory_is_active;

  return (
    <RailCard
      variant="publish"
      head={({ open, toggle }) => (
        <RailCardHead
          title="Publishing"
          variant="publish"
          open={open}
          onToggle={toggle}
          pill={
            <span
              className="font-condensed font-bold uppercase"
              style={{
                fontSize: 10,
                letterSpacing: '0.1em',
                padding: '3px 9px',
                borderRadius: 999,
                background: isActive ? '#75A29333' : '#9CA1A81F',
                color: isActive ? '#5C7E72' : '#9CA1A8',
                whiteSpace: 'nowrap',
              }}
            >
              {isActive ? 'Active' : 'Inactive'}
            </span>
          }
        />
      )}
    >
      <div style={{ padding: '14px 18px' }}>
        <dl
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '10px 14px',
            margin: 0,
          }}
        >
          <div style={{ gridColumn: '1 / -1' }}>
            <dt
              className="font-condensed font-bold uppercase text-text-muted"
              style={{ fontSize: 10, letterSpacing: '0.08em', margin: 0 }}
            >
              Type
            </dt>
            <dd style={{ margin: '4px 0 0' }}>
              <span
                className="font-condensed font-bold uppercase"
                style={{
                  fontSize: 10,
                  letterSpacing: '0.1em',
                  padding: '3px 9px',
                  borderRadius: 999,
                  background: '#2C3E5012',
                  color: '#2C3E50',
                }}
              >
                Audiobook
              </span>
            </dd>
          </div>

          <div style={{ gridColumn: '1 / -1' }}>
            <dt
              className="font-condensed font-bold uppercase text-text-muted"
              style={{ fontSize: 10, letterSpacing: '0.08em', margin: 0 }}
            >
              Parent
            </dt>
            <dd
              className="text-navy"
              style={{ margin: '2px 0 0', fontSize: 12.5, fontWeight: 500 }}
            >
              {parentCategoryTitle || '—'}
            </dd>
          </div>

          <div>
            <dt
              className="font-condensed font-bold uppercase text-text-muted"
              style={{ fontSize: 10, letterSpacing: '0.08em', margin: 0 }}
            >
              Chapters
            </dt>
            <dd
              className="text-navy"
              style={{ margin: '2px 0 0', fontSize: 12.5, fontWeight: 500 }}
            >
              {chapterCount ?? 0}
            </dd>
          </div>
          <div>
            <dt
              className="font-condensed font-bold uppercase text-text-muted"
              style={{ fontSize: 10, letterSpacing: '0.08em', margin: 0 }}
            >
              Length
            </dt>
            <dd
              className="text-navy"
              style={{ margin: '2px 0 0', fontSize: 12.5, fontWeight: 500 }}
            >
              {humanizeDuration(totalLengthSec)}
            </dd>
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <dt
              className="font-condensed font-bold uppercase text-text-muted"
              style={{ fontSize: 10, letterSpacing: '0.08em', margin: 0 }}
            >
              Quotes
            </dt>
            <dd
              className="text-navy"
              style={{ margin: '2px 0 0', fontSize: 12.5, fontWeight: 500 }}
            >
              {quoteCount ?? 0}
            </dd>
          </div>
        </dl>

        <div
          style={{
            marginTop: 14,
            paddingTop: 14,
            borderTop: '1px solid #ECE3D2',
            textAlign: 'center',
          }}
        >
          <button
            type="button"
            onClick={() => onToggleActive?.(!isActive)}
            className="font-semibold cursor-pointer"
            style={{
              fontSize: 12,
              color: isActive ? '#C4715B' : '#5C7E72',
              background: 'transparent',
              border: 0,
              padding: 0,
            }}
          >
            {isActive ? 'Deactivate audiobook' : 'Activate audiobook'}
          </button>
        </div>
      </div>
    </RailCard>
  );
}
